/**
 * Templates page: list message templates, create / edit / delete.
 * Entry point for DSOS elite templates (Keep Warm / Campaign).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMessageTemplates, useCreateTemplate, useUpdateTemplate, useDeleteTemplate } from '@/hooks/useMessageTemplates';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { LoadingState } from '@/components/LoadingState';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SmartCard } from '@/components/ui/smart-card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, Plus, Pencil, Trash2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import type { MessageTemplate } from '@/data/messageTemplates.service';
import { MessageTemplatesService } from '@/data/messageTemplates.service';
import { cn } from '@/lib/utils';

function extractVariables(body: string): string[] {
  const matches = body.match(/\{([a-z_]+)\}/gi) ?? [];
  return [...new Set(matches.map((m) => m.slice(1, -1).toLowerCase()))];
}

export default function Templates() {
  const { activeWorkspaceId } = useWorkspaceScope();
  const { data: templates, isLoading } = useMessageTemplates();
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [formName, setFormName] = useState('');
  const [formBody, setFormBody] = useState('');

  const safeTemplates = Array.isArray(templates) ? templates : [];

  const handleCreateSave = async () => {
    if (!formName.trim() || !formBody.trim()) {
      toast.error('Name and body are required');
      return;
    }
    try {
      await createTemplate.mutateAsync({
        name: formName.trim(),
        channel: 'whatsapp',
        body: formBody.trim(),
        category: 'reactivate',
        scope_type: 'keep_warm',
        variables: extractVariables(formBody),
      });
      toast.success('Template created');
      setFormName('');
      setFormBody('');
      setCreateOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to create template');
    }
  };

  const handleEditOpen = (t: MessageTemplate) => {
    setEditingTemplate(t);
    setFormName(t.name);
    setFormBody(t.body);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingTemplate || !formName.trim() || !formBody.trim()) return;
    try {
      await updateTemplate.mutateAsync({
        id: editingTemplate.id,
        updates: {
          name: formName.trim(),
          body: formBody.trim(),
          variables: extractVariables(formBody),
        },
      });
      toast.success('Template updated');
      setEditOpen(false);
      setEditingTemplate(null);
      setFormName('');
      setFormBody('');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to update template');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTemplate.mutateAsync(id);
      toast.success('Template deleted');
      setDeleteConfirmId(null);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to delete template');
    }
  };

  if (!activeWorkspaceId) return null;
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <LoadingState message="Loading templates..." />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <PageHeader
          title="Message templates"
          subtitle="Keep Warm & Campaign"
          description="Reusable templates for WhatsApp. Use in Keep Warm or campaigns."
        />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/keep-warm">
              <MessageSquare className="w-4 h-4 mr-2" />
              Keep Warm
            </Link>
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create template
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {safeTemplates.length === 0 ? (
          <SmartCard className="p-8 border-border/40">
            <div className="text-center space-y-4 max-w-md mx-auto">
              <MessageSquare className="w-12 h-12 text-muted-foreground/40 mx-auto" />
              <p className="text-sm text-muted-foreground">No templates yet. Create one to use in Keep Warm.</p>
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create template
              </Button>
            </div>
          </SmartCard>
        ) : (
          safeTemplates.map((t) => (
            <SmartCard key={t.id} className="p-4 border-border/40 flex flex-wrap items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground">{t.name}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {(t as any).scope_type ?? t.category}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{t.channel}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{t.body}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => handleEditOpen(t)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteConfirmId(t.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </SmartCard>
          ))
        )}
      </div>

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md gap-4">
          <DialogHeader>
            <DialogTitle>Create message template</DialogTitle>
            <DialogDescription>Add a reusable template. Use {'{first_name}'} for personalization.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                placeholder="e.g. Re-engage check-in"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="rounded-lg"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-body">Body</Label>
              <Textarea
                id="create-body"
                placeholder="Hi {first_name}, just checking in..."
                value={formBody}
                onChange={(e) => setFormBody(e.target.value)}
                rows={4}
                className="rounded-lg resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createTemplate.isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateSave}
              disabled={createTemplate.isPending || !formName.trim() || !formBody.trim()}
            >
              {createTemplate.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={editOpen} onOpenChange={(open) => !open && setEditingTemplate(null)}>
        <DialogContent className="sm:max-w-md gap-4">
          <DialogHeader>
            <DialogTitle>Edit template</DialogTitle>
            <DialogDescription>Update name and body. Use {'{first_name}'} for personalization.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="rounded-lg"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-body">Body</Label>
              <Textarea
                id="edit-body"
                value={formBody}
                onChange={(e) => setFormBody(e.target.value)}
                rows={4}
                className="rounded-lg resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={updateTemplate.isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={updateTemplate.isPending || !formName.trim() || !formBody.trim()}
            >
              {updateTemplate.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogDescription>This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} disabled={deleteTemplate.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              disabled={deleteTemplate.isPending}
            >
              {deleteTemplate.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
